import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FlaskConical,
  Palette,
  Link2,
  Plus,
  Trash2,
  CalendarDays,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useABTests, ABTestType } from '@/hooks/useABTests';
import { useQRStore, QRDesignSettings } from '@/stores/qrStore';
import { DesignTabs } from '@/components/qr/design/DesignTabs';

interface CreateABTestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qrCodeId?: string;
  baseDesignSettings?: QRDesignSettings;
  destinationUrl?: string;
}

type WizardStep = 'basics' | 'variants' | 'settings';

const VARIANT_LETTERS = ['A', 'B', 'C', 'D', 'E'];

interface VariantConfig {
  name: string;
  designSettings?: Record<string, unknown>;
  destinationUrl?: string;
}

export function CreateABTestModal({
  open,
  onOpenChange,
  qrCodeId,
  baseDesignSettings,
  destinationUrl,
}: CreateABTestModalProps) {
  const { createTest } = useABTests();
  const [step, setStep] = useState<WizardStep>('basics');
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [testName, setTestName] = useState('');
  const [testType, setTestType] = useState<ABTestType>('design');
  const [variantCount, setVariantCount] = useState(2);
  const [variants, setVariants] = useState<VariantConfig[]>([
    { name: 'A', designSettings: baseDesignSettings ? JSON.parse(JSON.stringify(baseDesignSettings)) : {}, destinationUrl },
    { name: 'B', designSettings: baseDesignSettings ? JSON.parse(JSON.stringify(baseDesignSettings)) : {}, destinationUrl },
  ]);
  const [trafficSplit, setTrafficSplit] = useState<Record<string, number>>({ A: 50, B: 50 });
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [activeVariant, setActiveVariant] = useState(0);

  const resetForm = () => {
    setStep('basics');
    setTestName('');
    setTestType('design');
    setVariantCount(2);
    setVariants([
      { name: 'A', designSettings: baseDesignSettings ? JSON.parse(JSON.stringify(baseDesignSettings)) : {}, destinationUrl },
      { name: 'B', designSettings: baseDesignSettings ? JSON.parse(JSON.stringify(baseDesignSettings)) : {}, destinationUrl },
    ]);
    setTrafficSplit({ A: 50, B: 50 });
    setHasEndDate(false);
    setEndDate(undefined);
    setActiveVariant(0);
  };

  const handleVariantCountChange = (count: number) => {
    setVariantCount(count);
    const newVariants: VariantConfig[] = [];
    const newSplit: Record<string, number> = {};
    const splitValue = Math.floor(100 / count);
    const remainder = 100 - splitValue * count;

    for (let i = 0; i < count; i++) {
      const letter = VARIANT_LETTERS[i];
      newVariants.push({
        name: letter,
        designSettings: variants[i]?.designSettings || (baseDesignSettings ? JSON.parse(JSON.stringify(baseDesignSettings)) : {}),
        destinationUrl: variants[i]?.destinationUrl || destinationUrl,
      });
      newSplit[letter] = splitValue + (i === 0 ? remainder : 0);
    }

    setVariants(newVariants);
    setTrafficSplit(newSplit);
  };

  const updateVariantDesign = (index: number, settings: Record<string, unknown>) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], designSettings: settings };
    setVariants(newVariants);
  };

  const updateVariantUrl = (index: number, url: string) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], destinationUrl: url };
    setVariants(newVariants);
  };

  const handleCreate = async () => {
    if (!testName.trim()) return;

    setIsCreating(true);
    try {
      await createTest({
        name: testName,
        qrCodeId,
        testType,
        trafficSplit,
        endDate: hasEndDate && endDate ? endDate.toISOString() : undefined,
        variants: variants.map((v) => ({
          variantName: v.name,
          designSettings: testType === 'design' ? v.designSettings : undefined,
          destinationUrl: v.destinationUrl,
        })),
      });
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create test:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 'basics':
        return testName.trim().length > 0;
      case 'variants':
        return variants.every((v) =>
          testType === 'destination' ? v.destinationUrl?.trim() : true
        );
      case 'settings':
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 'basics') setStep('variants');
    else if (step === 'variants') setStep('settings');
  };

  const prevStep = () => {
    if (step === 'settings') setStep('variants');
    else if (step === 'variants') setStep('basics');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-primary" />
            Create A/B Test
          </DialogTitle>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          {(['basics', 'variants', 'settings'] as WizardStep[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                  step === s
                    ? 'bg-primary text-primary-foreground'
                    : ['variants', 'settings'].indexOf(step) > i
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {i + 1}
              </div>
              {i < 2 && (
                <div
                  className={cn(
                    'w-12 h-0.5 mx-1',
                    ['variants', 'settings'].indexOf(step) > i ? 'bg-primary/50' : 'bg-muted'
                  )}
                />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Basics */}
          {step === 'basics' && (
            <motion.div
              key="basics"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <Label htmlFor="testName">Test Name</Label>
                <Input
                  id="testName"
                  placeholder="e.g., Menu QR Color Test"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                />
              </div>

              <div className="space-y-3">
                <Label>What do you want to test?</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setTestType('design')}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all',
                      testType === 'design'
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <Palette className="h-8 w-8 mb-2 text-primary" />
                    <h4 className="font-medium">Design Variants</h4>
                    <p className="text-sm text-muted-foreground">
                      Test different colors, shapes, frames
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTestType('destination')}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all',
                      testType === 'destination'
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-muted-foreground/30'
                    )}
                  >
                    <Link2 className="h-8 w-8 mb-2 text-primary" />
                    <h4 className="font-medium">Destination URLs</h4>
                    <p className="text-sm text-muted-foreground">
                      Test different landing pages
                    </p>
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Number of Variants: {variantCount}</Label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5].map((num) => (
                    <Button
                      key={num}
                      type="button"
                      variant={variantCount === num ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleVariantCountChange(num)}
                    >
                      {num} variants
                    </Button>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {VARIANT_LETTERS.slice(0, variantCount).map((letter) => (
                    <Badge key={letter} variant="secondary">
                      Variant {letter}
                    </Badge>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Variants */}
          {step === 'variants' && (
            <motion.div
              key="variants"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex gap-2 flex-wrap">
                {variants.map((v, i) => (
                  <Button
                    key={v.name}
                    variant={activeVariant === i ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveVariant(i)}
                  >
                    Variant {v.name}
                    {i === 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        Baseline
                      </Badge>
                    )}
                  </Button>
                ))}
              </div>

              <div className="border rounded-xl p-4 min-h-[200px]">
                {testType === 'design' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Customize design for Variant {variants[activeVariant].name}
                    </p>
                    <DesignTabs />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Label>Destination URL for Variant {variants[activeVariant].name}</Label>
                    <Input
                      placeholder="https://example.com/landing-page-a"
                      value={variants[activeVariant].destinationUrl || ''}
                      onChange={(e) => updateVariantUrl(activeVariant, e.target.value)}
                    />
                    <p className="text-sm text-muted-foreground">
                      Users will be randomly directed to different URLs based on traffic split.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Settings */}
          {step === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <Label>Traffic Split</Label>
                <div className="space-y-3">
                  {variants.map((v) => (
                    <div key={v.name} className="flex items-center gap-4">
                      <span className="w-24 text-sm font-medium">Variant {v.name}</span>
                      <Slider
                        value={[trafficSplit[v.name]]}
                        onValueChange={([value]) => {
                          // Adjust other variants proportionally
                          const others = variants.filter((x) => x.name !== v.name);
                          const remaining = 100 - value;
                          const perOther = Math.floor(remaining / others.length);
                          const newSplit: Record<string, number> = { [v.name]: value };
                          others.forEach((o, i) => {
                            newSplit[o.name] =
                              i === others.length - 1
                                ? remaining - perOther * (others.length - 1)
                                : perOther;
                          });
                          setTrafficSplit(newSplit);
                        }}
                        min={0}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-right text-sm">{trafficSplit[v.name]}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label>Set End Date</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically stop test on a specific date
                  </p>
                </div>
                <Switch checked={hasEndDate} onCheckedChange={setHasEndDate} />
              </div>

              {hasEndDate && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick an end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              )}

              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="font-medium">Test Summary</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Testing: {testType === 'design' ? 'QR Design' : 'Destination URLs'}</li>
                  <li>• {variantCount} variants with traffic split</li>
                  <li>• {hasEndDate && endDate ? `Ends: ${format(endDate, 'PPP')}` : 'Runs until stopped manually'}</li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 'basics' ? () => onOpenChange(false) : prevStep}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 'basics' ? 'Cancel' : 'Back'}
          </Button>

          {step === 'settings' ? (
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FlaskConical className="h-4 w-4 mr-2" />
                  Create Test
                </>
              )}
            </Button>
          ) : (
            <Button onClick={nextStep} disabled={!canProceed()}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
